/**
 * @file: useDashboardTasks.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useTaskStateTrackingContext } from '@euler/functions/TaskStateTrackingContext';
import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { useRefreshFeedback } from '@euler/functions/useRefreshFeedback';
import { TaskStatusInfo, UpdateOrderRequest } from '@euler/model';
import { VehicleReceptionOrder } from '@euler/model/entity';
import { InspectionTaskStatus } from '@euler/model/enum';
import { taskListItemFromTask } from '@euler/model/util';
import { useServiceFactory } from '@euler/services/factory';
import {
  dateValueEquals,
  delayed,
  isNotNull,
  onErrorIgnore,
  safeMarkDevFlag,
} from '@euler/utils';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useRef } from 'react';

const kIncludeCanceledTasks = safeMarkDevFlag(true);

export function useDashboardTasks({
  tag,
  assignedToMe,
  limit,
  onRefresh,
}: {
  tag?: string;
  assignedToMe?: boolean;
  limit: number;
  onRefresh: () => void;
}) {
  const { taskService } = useServiceFactory();
  const playRefreshFeedback = useRefreshFeedback();
  const { user } = useCurrentUserDangerously();
  const assignedTo = assignedToMe && user.memberId ? user.memberId : undefined;
  const query = useCallback(
    async (offset: number) => {
      return await taskService
        .list(
          {
            assignedTo,
            tag,
            status: [
              InspectionTaskStatus.Pending,
              InspectionTaskStatus.InProgress,
              kIncludeCanceledTasks
                ? InspectionTaskStatus.Cancelled
                : undefined,
            ].filter(isNotNull),
          },
          offset,
          limit,
        )
        .then(tasks => tasks.map(taskListItemFromTask));
    },
    [taskService, assignedTo, tag, limit],
  );

  const { fetch, refresh, fetchMore, fetchSinceLastError, update, ...list } =
    useInfiniteList({
      limit,
      query,
      initialLoading: true,
    });

  const onScrollToBottom = useCallback(() => {
    fetchMore().catch(onErrorIgnore);
  }, [fetchMore]);

  useEffect(() => {
    delayed(() => {
      fetch().catch(onErrorIgnore);
    }, 100);
  }, [fetch]);

  const orderContext = useOrderContext();
  useEffect(() => {
    const sub = orderContext.event$.subscribe(event => {
      __DEV__ && console.log('dashboard received order event: ', event);
      if (event.type === 'created') {
        onRefresh();
        delayed(() => {
          fetch().then(playRefreshFeedback).catch(onErrorIgnore);
        }, 100);
      } else if (
        event.isMainOrder &&
        (event.type === 'finished' || event.type === 'rollback')
      ) {
        onRefresh();
        fetch().catch(onErrorIgnore);
      } else if (event.type === 'updated' && event.isMainOrder) {
        update(item => {
          if (item.orderNo === event.orderNo) {
            // only update necessary information that is used in task list.
            const request: UpdateOrderRequest = event.extra.request;
            const updated = event.extra.order as VehicleReceptionOrder;
            if ('licensePlateNo' in request) {
              item.licensePlateNo = request.licensePlateNo!;
            }
            if ('serviceAgentId' in request) {
              item.serviceAgentName = updated.serviceAgentName;
            }
            if ('tags' in request) {
              item.tags = updated.tags;
            }
            if ('vehicleMileage' in request) {
              item.vehicleMileage = request.vehicleMileage!;
            }
            if ('estimatedFinishTime' in request) {
              item.estimatedFinishTime =
                request.estimatedFinishTime ?? undefined;
            }
            if (event.extra.hasVehicleInfoChanges) {
              item.vehicleName = updated.vehicleName;
              item.vehicleBrandName = updated.vehicleBrandName;
              item.vehicleModel = updated.vehicleModel;
              item.vehicleBrandLogo = updated.vehicleBrandLogo ?? undefined;
              item.vehicleImageUrl = updated.vehicleImageUrl ?? undefined;
              item.vehicleManufacturer =
                updated.vehicleManufacturer ?? undefined;
              item.vehicleFuelType = updated.vehicleFuelType ?? undefined;
              item.vehicleModelYear = updated.vehicleModelYear ?? undefined;
              item.vehicleDisplacement =
                updated.vehicleDisplacement ?? undefined;
              item.vehicleTrimLevel = updated.vehicleTrimLevel ?? undefined;
            }
          }
          return undefined;
        });
      }
    });
    return () => sub.unsubscribe();
  }, [fetch, onRefresh, orderContext, playRefreshFeedback, update]);

  // record the pending changes.
  const pendingChanges = useRef<Map<string, TaskStatusInfo>>();
  const { statusChanges$ } = useTaskStateTrackingContext();
  useEffect(() => {
    const sub = statusChanges$.subscribe(changes => {
      pendingChanges.current = changes;
    });
    return () => sub.unsubscribe();
  }, [statusChanges$]);

  const onFocus = useCallback(() => {
    // handle pending changes.
    if (pendingChanges.current == null) return;

    const map = pendingChanges.current;
    const values = [...map.values()];

    if (
      values.some(
        x =>
          x.status === InspectionTaskStatus.Finished ||
          x.status === InspectionTaskStatus.Cancelled,
      )
    ) {
      // refresh the list if some tasks are finished.
      onRefresh();
      delayed(() => {
        refresh().then(playRefreshFeedback).catch(onErrorIgnore);
      }, 100);
    } else {
      update(item => {
        const statusInfo = map.get(item.id);
        if (!statusInfo) return undefined;
        if (item.status !== statusInfo.status) {
          item.status = statusInfo.status;
        }
        if (item.preInspectionStatus !== statusInfo.preInspectionStatus) {
          item.preInspectionStatus = statusInfo.preInspectionStatus;
        }
        if (item.inspectionStatus !== statusInfo.inspectionStatus) {
          item.inspectionStatus = statusInfo.inspectionStatus;
        }
        if (item.constructionStatus !== statusInfo.constructionStatus) {
          item.constructionStatus = statusInfo.constructionStatus;
        }
        if (item.deliveryCheckStatus !== statusInfo.deliveryCheckStatus) {
          item.deliveryCheckStatus = statusInfo.deliveryCheckStatus;
        }
        if (item.isObdInspected !== statusInfo.isObdInspected) {
          item.isObdInspected = statusInfo.isObdInspected;
        }
        if (
          item.isConstructionJobScheduled !==
          statusInfo.isConstructionJobScheduled
        ) {
          item.isConstructionJobScheduled =
            statusInfo.isConstructionJobScheduled;
        }
        if (!dateValueEquals(item.finishedAt, statusInfo.finishedAt)) {
          item.finishedAt = statusInfo.finishedAt;
        }
      });
    }
    pendingChanges.current = undefined;
  }, [onRefresh, playRefreshFeedback, refresh, update]);

  useFocusEffect(onFocus);

  return {
    onScrollToBottom,
    fetch,
    refresh,
    update,
    fetchMore,
    fetchSinceLastError,
    ...list,
  };
}
